'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { AnimatePresence, motion } from 'motion/react'
import { useTranslations } from 'next-intl'
import { useCallback, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'
import { useStudentDiscountSubmitMutation } from '@/api/student-discounts/student-discounts-submit-mutation'
import { Button } from '@/components/button'
import { Input } from '@/components/input/input'
import { Form, FormProvider } from '@/components/RHF/form'
import { FormField } from '@/components/RHF/form-field'
import { Switch } from '@/components/switch'
import { UniversitySearch } from '@/features/student-discounts/university-search'
import { extractApiErrorCode } from '@/utils/api/api-error-code'
import { noOp } from '@/utils/functions/noOp'

const getStudentDiscountsSchema = (t: (key: string) => string) =>
    z.object({
        firstName: z.string().min(1, t('form.firstName.error')),
        lastName: z.string().min(1, t('form.lastName.error')),
        university: z.object(
            { value: z.string(), label: z.string() },
            { message: t('form.university.error') }
        ),
        universityEmail: z
            .string()
            .min(1, t('form.universityEmail.error'))
            .email(t('form.universityEmail.error')),
        hasNeotasteAccount: z.boolean(),
        // Optional — only relevant when the user already has an account. Validated
        // as an email only when something was entered.
        neotasteEmail: z
            .string()
            .refine((value) => value === '' || z.string().email().safeParse(value).success, {
                message: t('form.neotasteEmail.error'),
            }),
    })

type StudentDiscountsFormValues = z.infer<ReturnType<typeof getStudentDiscountsSchema>>

/**
 * Maps a submit failure to a message describing what actually went wrong, so a
 * student knows whether to fix their email, pick a different university, or just
 * wait — rather than a single opaque "something went wrong". Falls back to a
 * generic message for unexpected/server errors.
 */
const getSubmitErrorMessage = (t: (key: string) => string, error: unknown): string => {
    switch (extractApiErrorCode(error)) {
        case 'studentEmailInvalid':
            return t('form.errors.emailNotRecognized')
        case 'studentUniversityMismatch':
            return t('form.errors.universityMismatch')
        case 'studentVerificationRateLimited':
            return t('form.errors.rateLimited')
        case 'validation':
            return t('form.errors.validation')
        default:
            return t('form.errors.generic')
    }
}

export const StudentDiscountsForm = () => {
    const t = useTranslations('student-discounts')

    const submitMutation = useStudentDiscountSubmitMutation()
    const [submittedEmail, setSubmittedEmail] = useState<string | null>(null)
    const [submitError, setSubmitError] = useState<string | null>(null)

    const form = useForm<StudentDiscountsFormValues>({
        mode: 'onBlur',
        resolver: zodResolver(getStudentDiscountsSchema(t)),
        defaultValues: {
            firstName: '',
            lastName: '',
            university: undefined,
            universityEmail: '',
            hasNeotasteAccount: false,
            neotasteEmail: '',
        },
    })

    const hasNeotasteAccount = form.watch('hasNeotasteAccount')

    const handleSubmit: SubmitHandler<StudentDiscountsFormValues> = useCallback(
        async (values) => {
            setSubmitError(null)

            try {
                await submitMutation.mutateAsync({
                    body: {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        universityId: values.university.value,
                        universityEmail: values.universityEmail,
                        neotasteEmail: values.hasNeotasteAccount
                            ? values.neotasteEmail || null
                            : null,
                    },
                })

                setSubmittedEmail(values.universityEmail)
            } catch (error) {
                setSubmitError(getSubmitErrorMessage(t, error))
            }
        },
        [submitMutation, t]
    )

    if (submittedEmail) {
        return (
            <div className="flex w-full flex-col items-center gap-3 rounded-l bg-black/5 p-8 text-center">
                <h2 className="text-[20px] font-semibold">{t('confirmation.title')}</h2>
                <p className="text-black/60">{t('confirmation.body', { email: submittedEmail })}</p>
            </div>
        )
    }

    return (
        <FormProvider {...form}>
            <Form className="flex w-full flex-col gap-3" onSubmit={form.handleSubmit(handleSubmit)}>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <FormField
                        control={form.control}
                        name="firstName"
                        styleType="small"
                        label={t('form.firstName.label')}
                        render={(fieldProps) => (
                            <Input
                                placeholder={t('form.firstName.placeholder')}
                                {...fieldProps.field}
                                onValueChange={fieldProps.field.onChange}
                            />
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="lastName"
                        styleType="small"
                        label={t('form.lastName.label')}
                        render={(fieldProps) => (
                            <Input
                                placeholder={t('form.lastName.placeholder')}
                                {...fieldProps.field}
                                onValueChange={fieldProps.field.onChange}
                            />
                        )}
                    />
                </div>

                <FormField
                    control={form.control}
                    name="university"
                    styleType="small"
                    label={t('form.university.label')}
                    render={(fieldProps) => (
                        <UniversitySearch
                            value={fieldProps.field.value}
                            onSelect={fieldProps.field.onChange}
                            onBlur={fieldProps.field.onBlur}
                            placeholder={t('form.university.placeholder')}
                            noResultsLabel={t('form.university.noResults')}
                        />
                    )}
                />

                <FormField
                    control={form.control}
                    name="universityEmail"
                    styleType="small"
                    label={t('form.universityEmail.label')}
                    description={t('form.universityEmail.note')}
                    render={(fieldProps) => (
                        <Input
                            htmlType="email"
                            placeholder={t('form.universityEmail.placeholder')}
                            {...fieldProps.field}
                            onValueChange={fieldProps.field.onChange}
                        />
                    )}
                />

                <FormField
                    control={form.control}
                    name="hasNeotasteAccount"
                    styleType="small"
                    render={(fieldProps) => (
                        <Switch
                            checked={fieldProps.field.value}
                            onToggle={() => {
                                const next = !fieldProps.field.value

                                fieldProps.field.onChange(next)

                                // Reset the (now hidden) email when toggling off so a stale
                                // value can't linger in the payload.
                                if (!next) {
                                    form.setValue('neotasteEmail', '', { shouldValidate: true })
                                }
                            }}
                            label={t('form.hasNeotasteAccount.label')}
                        />
                    )}
                />

                <AnimatePresence initial={false}>
                    {hasNeotasteAccount && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                        >
                            <FormField
                                control={form.control}
                                name="neotasteEmail"
                                styleType="small"
                                label={t('form.neotasteEmail.label')}
                                render={(fieldProps) => (
                                    <Input
                                        htmlType="email"
                                        placeholder={t('form.neotasteEmail.placeholder')}
                                        {...fieldProps.field}
                                        onValueChange={fieldProps.field.onChange}
                                    />
                                )}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>

                {submitError && (
                    <p className="text-center text-[14px] text-red-500">{submitError}</p>
                )}

                <Button
                    onClick={noOp}
                    styleType="unstyled"
                    className="mt-2 h-[48px] w-full rounded-l bg-neotaste p-3 font-semibold text-black"
                    type="submit"
                    disabled={!form.formState.isValid}
                    isLoading={form.formState.isSubmitting}
                >
                    {t('form.submit')}
                </Button>
            </Form>
        </FormProvider>
    )
}
